<template>
  <div class="explore-page">
    <Hero :title="$t('explore.hero.title')" :description="$t('explore.hero.description')">
      <template #image>
        <img src="/images/explore/hero.png" :alt="$t('explore.hero.title')" loading="eager" />
      </template>
    </Hero>
    <div class="explore-tab">
      <div class="explore-tab__container container">
        <div class="explore-tab__title">{{ $t("explore.filter") }}</div>
        <div class="explore-tab__navigation">
          <NuxtLinkLocale to="/explore" class="explore-tab__navigation-item" :class="{ active: !category }">{{ $t("explore.all") }}</NuxtLinkLocale>
          <NuxtLinkLocale to="/explore/city" class="explore-tab__navigation-item" :class="{ active: category === 'city' }">
            {{ $t("explore.city") }}
          </NuxtLinkLocale>
          <NuxtLinkLocale to="/explore/event" class="explore-tab__navigation-item" :class="{ active: category === 'event' }">
            {{ $t("explore.event") }}
          </NuxtLinkLocale>
        </div>
      </div>
    </div>
    <ElementsContainer>
      <template v-if="explore && explore.length > 0">
        <div class="flex flex-wrap -mx-3 gap-y-10 lg:gap-y-15">
          <div v-for="item in explore" :key="item.id" class="w-full md:w-1/2 px-3">
            <ExploreCard v-bind="item" />
          </div>
        </div>
      </template>
      <template v-else>
        <ElementsError
          :title="$t('explore.notFound.title')"
          :description="$t('explore.notFound.description')"
          :link="{ url: '/explore', title: $t('explore.notFound.link') }"
        />
      </template>
    </ElementsContainer>
  </div>
</template>

<script setup lang="ts">
  import fetchApi from "~/utils/fetchApi"

  const { t } = useI18n()
  const route = useRoute()
  const categoryParam = route.params.category
  const category = computed(() => {
    if (Array.isArray(categoryParam)) {
      return categoryParam[0]
    }
    return categoryParam
  })

  const query: any = {
    sort: ["id:desc"]
  }
  if (category.value) {
    query["filters[category][$eq]"] = category.value
  }

  const { data: responseData } = await useAsyncData(`explore-${category.value || "all"}`, () =>
    fetchApi<any[]>("explores", {
      wrappedByKey: "data",
      populate: "*",
      query
    })
  )

  const explore = computed(() => responseData.value || [])

  useSeoMeta({
    title: () => t("explore.hero.title"),
    description: () => t("explore.hero.description")
  })
</script>

<style lang="scss" scoped>
  .explore {
    &-tab {
      border-bottom: 1px solid #e0e0e0;
      background: #fff;
      z-index: 100;
      position: relative;
      display: flex;
      align-items: center;
      overflow: hidden;

      &__container {
        display: flex;
        align-items: center;
        gap: 48px;
      }
      &__title {
        color: #000;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        letter-spacing: 1.68px;
        text-transform: uppercase;
      }
      &__navigation {
        display: flex;
        gap: 28px;

        &-item {
          margin: 0 4px;
          font-size: 13px;
          font-weight: 700;
          line-height: 16px;
          letter-spacing: 1.82px;
          text-transform: uppercase;
          height: 76px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(85, 85, 85, 0.95);
          position: relative;
          transition: all 0.2s;

          &.active {
            color: #282828;
            border-top: 3px solid transparent;
            border-bottom: 3px solid #282828;
          }
        }
      }
    }

    @include media("lg") {
      &-tab {
        &__container {
          gap: 24px;
        }
        &__title {
          font-size: 12px;
          line-height: 16px;
          letter-spacing: 1px;
        }
        &__navigation {
          gap: 12px;
          overflow: auto;
          margin: 0 -16px;

          &-item {
            font-size: 12px;
            line-height: 16px;
            letter-spacing: 1px;
            height: 54px;

            &.active {
              border-top: 2px solid transparent;
              border-bottom: 2px solid #282828;
            }
          }

          &::-webkit-scrollbar {
            width: 0;
            height: 0;
          }
        }
      }
    }
  }
</style>
